/**
 * Database configuration data for the Settings page.
 * Each config can be assigned to projects, teams, or individual users.
 */

export const DB_TYPES = [
  { value: "athena", label: "Amazon Athena", icon: "🔥" },
  { value: "postgresql", label: "PostgreSQL", icon: "🐘" },
  // { value: 'snowflake',  label: 'Snowflake', icon: '❄️' },
  // { value: 'mongodb',    label: 'MongoDB', icon: '🍃' },
];

export const ASSIGNMENT_OPTIONS = {
  projects: [
    { value: "proj-demand-forecast", label: "Demand Forecast Pipeline" },
    { value: "proj-inventory", label: "Inventory Reconciliation" },
    { value: "proj-supplier", label: "Supplier Lead Time Tracker" },
    { value: "proj-external", label: "External Factors Scraper" },
  ],
  teams: [
    { value: "team-data", label: "Data Engineering" },
    { value: "team-analytics", label: "Analytics" },
    { value: "team-ops", label: "Operations" },
    { value: "team-supply", label: "Supply Chain" },
  ],
  persons: [
    { value: "user-finn", label: "Finn Taylor" },
    { value: "user-sarah", label: "Sarah Chen" },
    { value: "user-james", label: "James Rivera" },
    { value: "user-priya", label: "Priya Patel" },
  ],
};

// Sample saved DB connections
export const INITIAL_DB_CONFIGS = [
  {
    id: "db-1",
    type: "athena",
    label: "Amazon Athena",
    icon: "🔥",
    connectionId: "lake_db_1_qhr",
    fields: {
      s3StagingDir: "s3://matalan-aria-ingestiq-athena-results/athena-results/",
      awsRegion: "eu-west-2",
      catalog: "AwsDataCatalog",
      databaseName: "lake_db",
      workgroup: "ingestiq",
      semanticLayer: "",
    },
    assignments: {
      projects: ["proj-demand-forecast"],
      teams: ["team-data", "team-analytics"],
      persons: ["user-finn"],
    },
  },
  {
    id: "db-2",
    type: "athena",
    label: "Amazon Athena",
    icon: "🔥",
    connectionId: "lake_db_1_hup",
    fields: {
      s3StagingDir: "s3://matalan-aria-ingestiq-athena-results/athena-results/",
      awsRegion: "eu-west-2",
      catalog: "AwsDataCatalog",
      databaseName: "lake_db",
      workgroup: "ingestiq",
      semanticLayer: "",
    },
    assignments: {
      projects: ["proj-inventory"],
      teams: ["team-ops"],
      persons: ["user-finn", "user-sarah"],
    },
  },
  {
    id: "db-3",
    type: "postgresql",
    label: "PostgreSQL",
    icon: "🐘",
    connectionId: "ipdb_pg_01",
    fields: {
      hostname:
        "intellegent-platflom-cluster.cluster-czxnrwlwgsx.eu-west-2.rds.amazonaws.com",
      port: "5432",
      username: "postgres",
      password: "••••••••",
      databaseName: "ipdb",
      semanticLayer: "",
    },
    assignments: {
      projects: ["proj-supplier"],
      teams: ["team-supply"],
      persons: ["user-finn", "user-james"],
    },
  },
];

/**
 * Returns configs assigned to a specific user.
 * In a real app, this would filter by the logged-in user's ID.
 */
export function getDbConfigsForUser(configs, userId = "user-finn") {
  return configs.filter((cfg) => {
    const a = cfg.assignments;
    return (
      a.persons.includes(userId) || a.teams.length > 0 || a.projects.length > 0
    );
  });
}

/**
 * Field definitions per database type for the form.
 */
export const DB_FIELD_DEFS = {
  postgresql: [
    {
      key: "hostname",
      label: "Hostname",
      placeholder: "db.company.com",
      half: true,
    },
    { key: "port", label: "Port", placeholder: "5432", half: true },
    { key: "username", label: "Username", placeholder: "db_user", half: true },
    {
      key: "password",
      label: "Password",
      placeholder: "••••••••",
      half: true,
      type: "password",
    },
    {
      key: "databaseName",
      label: "Database Name",
      placeholder: "analytics",
      half: false,
    },
    {
      key: "semanticLayer",
      label: "Semantic layer (optional — generated automatically after discovery)",
      placeholder:
        "Leave empty to use the generated description. Anything you write here overrides it (min 200 characters).",
      textarea: true,
    },
  ],
  athena: [
    {
      key: "s3StagingDir",
      label: "S3 Staging Directory",
      placeholder: "s3://bucket/path/",
      half: true,
    },
    {
      key: "awsRegion",
      label: "AWS Region",
      placeholder: "us-east-1",
      half: true,
    },
    {
      key: "catalog",
      label: "Catalog",
      placeholder: "AwsDataCatalog",
      half: true,
    },
    {
      key: "databaseName",
      label: "Database Name",
      placeholder: "analytics_db",
      half: true,
    },
    {
      key: "workgroup",
      label: "Workgroup",
      placeholder: "my_workgroup",
      half: false,
    },
    {
      key: "semanticLayer",
      label: "Semantic Layer (Optional)",
      placeholder: "Describe your Database tables and relationships",
      textarea: true,
    },
  ],
  snowflake: [
    {
      key: "account",
      label: "Account",
      placeholder: "org-account",
      half: true,
    },
    {
      key: "warehouse",
      label: "Warehouse",
      placeholder: "COMPUTE_WH",
      half: true,
    },
    { key: "username", label: "Username", placeholder: "db_user", half: true },
    {
      key: "password",
      label: "Password",
      placeholder: "••••••••",
      half: true,
      type: "password",
    },
    {
      key: "databaseName",
      label: "Database Name",
      placeholder: "analytics",
      half: true,
    },
    { key: "schema", label: "Schema", placeholder: "PUBLIC", half: true },
    {
      key: "semanticLayer",
      label: "Semantic Layer (Optional)",
      placeholder: "Describe your Database tables and relationships",
      textarea: true,
    },
  ],
  mongodb: [
    {
      key: "connectionString",
      label: "Connection String",
      placeholder: "mongodb+srv://...",
      half: false,
    },
    {
      key: "databaseName",
      label: "Database Name",
      placeholder: "analytics",
      half: false,
    },
    {
      key: "semanticLayer",
      label: "Semantic Layer (Optional)",
      placeholder: "Describe your Database tables and relationships",
      textarea: true,
    },
  ],
};
